import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { X } from "lucide-react";
import { resolveImage } from "@/lib/api";

const RecipeModal = ({ recipe, onClose }) => {
  useEffect(() => {
    if (recipe) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [recipe]);

  return (
    <AnimatePresence>
      {recipe && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="recipe-modal"
        >
          <div className="absolute inset-0 bg-[#1A1A1A]/60 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            className="relative bg-white w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto rounded-t-[28px] sm:rounded-[28px] shadow-2xl"
            initial={{ y: "100%", opacity: 0.5 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="relative h-48 sm:h-60">
              {resolveImage(recipe.image_url) ? (
                <img src={resolveImage(recipe.image_url)} alt={recipe.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#8B5CF6] to-[#FF3D68]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow hover:scale-110 active:scale-95 transition-transform"
                data-testid="recipe-modal-close"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-[#1A1A1A]" />
              </button>
              <span className="absolute bottom-4 left-5 bg-[#8B5CF6] text-white font-medium text-xs px-3 py-1 rounded-full">Free Recipe</span>
            </div>

            <div className="p-6 sm:p-8">
              <h2 className="font-display font-extrabold text-3xl text-[#1A1A1A] leading-tight">{recipe.title}</h2>
              <p className="mt-2 text-[#1A1A1A]/70 leading-relaxed">{recipe.description}</p>

              <div className="mt-7">
                <h3 className="font-display font-bold text-lg text-[#FF7A1A] mb-3">Ingredients</h3>
                <ul className="space-y-2">
                  {(recipe.ingredients || []).map((ing, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#1A1A1A]/80">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#7ED321] flex-shrink-0" />
                      <span>{ing}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-7">
                <h3 className="font-display font-bold text-lg text-[#FF7A1A] mb-3">Steps</h3>
                <ol className="space-y-4">
                  {(recipe.steps || []).map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FFC93C] text-[#1A1A1A] font-display font-extrabold text-sm flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-[#1A1A1A]/80 leading-relaxed pt-1">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecipeModal;
